import { useCallback, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import {
  getIsPlanErknmStatusInProcess,
  getIsPlanErknmStatusUnderConsideration,
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusApproved,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { PlanStatusEnum } from 'APIPackage/PlanListModule/enums/PlanStatusEnum';
import { IConsiderPlan } from 'APIPackage/PlanListModule/interfaces/IConsiderPlan';
import { IKnmPlanInfo } from 'APIPackage/PlanListModule/interfaces/IKnmPlanInfo';
import { IPlanStatusUpdate } from 'APIPackage/PlanListModule/interfaces/IPlanStatusUpdate';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { ChangePlanStatusModals } from 'CorePackage/ChangePlanStatusModalsModule/components/ChangePlanStatusModals';
import { ChangePlanStatusTypesEnum } from 'CorePackage/ChangePlanStatusModalsModule/enums/ChangePlanStatusTypesEnum';
import { IChangePlanStatusModalValues } from 'CorePackage/ChangePlanStatusModalsModule/interfaces/IChangePlanStatusModalValues';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { INotification } from 'CorePackage/NotificationModule/interfaces/INotification';
import { getErpPlanLabel } from 'PlanListPackage/MainModule/common/mapPlanStatus';
import { IKnmPlan } from 'PlanListPackage/MainModule/interfaces/IKnmPlan';
import {
  getPlanData,
  getPlanDataErknmStatus,
  getPlanFormRef,
  getPlanInfo,
  getPlanIsPrivilegesKno,
  getPlanWatchOnlyByTestUser,
} from 'PlanPackage/MainModule/selectors/planSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { planUpdateErknmPlanStatus, planUpdatePlanStatusToConsider } from '../actions/planActions';
import styles from './PlanHeaderButtons.module.scss';
import { PlanHeaderDropdown } from './PlanHeaderDropdown';
import { changePlanStatus } from '../common/changePlanStatus';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IPlanFormRef } from '../interfaces/IPlanFormRef';
import { PLAN_SIGN_IN_BROWSER_REQUIRED_FIELDS_ERROR } from '../../../CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';

interface IPlanHeaderButtonsProps {
  handleOpenSignModal: () => void;
  handleUploadButtonClick: () => void;
  handleOpenSignInfoModal: () => void;
  handleAddKnmButtonClick: () => void;
  handleOpenSignUploadModal: () => void;
  handleOpenChangeKnoModal: () => void;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  plan: IKnmPlan | undefined;
  planErknmStatus: ErknmPlanStatusesEnum | undefined;
  planInfo: IKnmPlanInfo | undefined;
  isPrivilegesKno?: boolean;
  settings: IAdminSetting[] | undefined;
  isWatchOnlyByTestUser: boolean;
  formRef: IPlanFormRef | undefined;
}

interface IPlanStatusInfo {
  id: number;
  newStatus: ErknmPlanStatusesEnum;
  knmAmount: number;
}

const PlanHeaderButtonsWithoutStore = ({
  handleOpenSignModal,
  handleUploadButtonClick,
  handleOpenSignInfoModal,
  handleAddKnmButtonClick,
  handleOpenSignUploadModal,
  handleOpenChangeKnoModal,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  plan,
  planErknmStatus,
  planInfo,
  isPrivilegesKno,
  settings,
  isWatchOnlyByTestUser,
  formRef,
}: IPlanHeaderButtonsProps) => {
  const isPlanErknmStatusInProcess: boolean = getIsPlanErknmStatusInProcess(planErknmStatus);
  const isPlanErknmStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(planErknmStatus);
  const isPlanErknmStatusConsidered: boolean = getIsPlanErknmStatusConsidered(planErknmStatus);
  const isPlanErknmStatusApproved: boolean = getIsPlanErknmStatusApproved(planErknmStatus);
  const isPlanSigned: boolean = !!plan?.knmPlanData?.signedXlsxFile?.signed;

  const isShowChangeStatusButton =
    !isPlanErknmStatusApproved && plan?.status !== getErpPlanLabel(PlanStatusEnum.APPROVED) && !isWatchOnlyByTestUser;
  const isShowSignButton =
    !isProsecutorOrSuperProsecutor &&
    ((isPlanErknmStatusApproved && !isPlanSigned) ||
      (isShowChangeStatusButton && (isPlanErknmStatusInProcess || isPlanErknmStatusConsidered))) &&
    !isWatchOnlyByTestUser;

  const dispatch = useDispatch<Dispatch<RootActions>>();

  const addNotice: (notice: INotification) => void = useCallback(
    (notice: INotification) => dispatch(addNoticeAction(notice)),
    [dispatch],
  );

  const updateErknmPlanStatus: (id: number, data: IPlanStatusUpdate, knmAmount: number) => void = useCallback(
    (id: number, data: IPlanStatusUpdate, knmAmount: number) =>
      dispatch(planUpdateErknmPlanStatus(id, data, knmAmount)),
    [dispatch],
  );

  const updatePlanStatusToConsider: (id: number, data: IConsiderPlan) => void = useCallback(
    (id: number, data: IConsiderPlan) => dispatch(planUpdatePlanStatusToConsider(id, data)),
    [dispatch],
  );

  const [planStatusInfo, setPlanStatusInfo] = useState<IPlanStatusInfo | null>(null);
  const [activeModal, setActiveModal] = useState<ChangePlanStatusTypesEnum | null>(null);

  const [errorTextForModal, setErrorTextForModal] = useState<string>();

  const makeChangeStatusButtonClickHandler = (newStatus: ErknmPlanStatusesEnum) => () =>
    changePlanStatus({
      plan,
      planInfo,
      newStatus,
      isProsecutorOrSuperProsecutor,
      isPrivilegesKno,
      addNotice,
      setPlanStatusInfo,
      setActiveModal,
      setErrorTextForModal,
      settings,
      formRef,
    });

  const renderChangeStatusButton = () => {
    if (plan && !isPlanErknmStatusApproved) {
      if (isSupervisor && (isPlanErknmStatusInProcess || isPlanErknmStatusConsidered)) {
        return (
          <Button
            className={styles.PublishButton}
            color="success"
            text={
              (isPlanErknmStatusInProcess && 'Отправить на рассмотрение') ||
              (isPlanErknmStatusConsidered && 'Утвердить')
            }
            id="planChangeStatusButton"
            disabled={plan.deleted}
            type="button"
            onClick={makeChangeStatusButtonClickHandler(
              isPlanErknmStatusInProcess ? ErknmPlanStatusesEnum.UNDER_CONSIDERATION : ErknmPlanStatusesEnum.APPROVED,
            )}
          />
        );
      }
      if (isProsecutorOrSuperProsecutor && isPlanErknmStatusUnderConsideration) {
        return (
          <Button
            className={styles.PublishButton}
            color="success"
            text="Рассмотрено"
            disabled={plan.deleted}
            type="button"
            onClick={makeChangeStatusButtonClickHandler(ErknmPlanStatusesEnum.CONSIDERED)}
          />
        );
      }
    }
  };

  const handleChangePlanStatusApply = useCallback(
    ({ comment, documents }: IChangePlanStatusModalValues) => {
      if (planStatusInfo) {
        const { id, newStatus, knmAmount } = planStatusInfo;

        if (plan?.knmPlanData?.version === KnmVersionsEnum.ERKNM) {
          updateErknmPlanStatus(
            id,
            {
              changed: null,
              oldStatus: null,
              newStatus,
              comment,
              documents,
            },
            knmAmount,
          );
        }
        setActiveModal(null);
      }
    },
    [plan?.knmPlanData?.version, planStatusInfo, updateErknmPlanStatus],
  );

  const handleChangePlanStatusToConsiderApply = useCallback(
    (data: IConsiderPlan) => {
      if (planStatusInfo) {
        const { id } = planStatusInfo;

        if (plan?.knmPlanData?.version === KnmVersionsEnum.ERKNM) {
          updatePlanStatusToConsider(id, data);
        }
        setActiveModal(null);
      }
    },
    [plan?.knmPlanData?.version, planStatusInfo, updatePlanStatusToConsider],
  );

  const handleChangePlanStatusClose = useCallback(() => {
    setActiveModal(null);
    setErrorTextForModal('');
  }, []);

  return (
    <div className={styles.Container}>
      {isShowSignButton && (
        <Button
          className={styles.Button}
          color="primary"
          text="Подписать в браузере"
          id="signButton"
          disabled={plan?.deleted || !!plan?.signed}
          onClick={() => {
            if (!formRef?.current.errors || Object.keys(formRef?.current.errors).length > 0) {
              dispatch(
                addNoticeAction({
                  renderContent: PLAN_SIGN_IN_BROWSER_REQUIRED_FIELDS_ERROR,
                  noticeType: NoticeTypesEnum.WARNING,
                  messageType: MessageTypesEnum.SAVE_KNM,
                }),
              );
            }
            handleOpenSignModal();
          }}
        />
      )}
      {isShowChangeStatusButton && renderChangeStatusButton()}
      <PlanHeaderDropdown
        handleOpenSignModal={handleOpenSignModal}
        handleUploadButtonClick={handleUploadButtonClick}
        handleOpenSignInfoModal={handleOpenSignInfoModal}
        handleAddKnmButtonClick={handleAddKnmButtonClick}
        handleOpenSignUploadModal={handleOpenSignUploadModal}
        handleOpenChangeKnoModal={handleOpenChangeKnoModal}
      />
      <ChangePlanStatusModals
        activeModal={activeModal}
        planStatus={planStatusInfo?.newStatus}
        errorText={errorTextForModal}
        onChangeApply={handleChangePlanStatusApply}
        onChangeToConsiderApply={handleChangePlanStatusToConsiderApply}
        onClose={handleChangePlanStatusClose}
      />
    </div>
  );
};

const mapsStateToProps = (state: IRootState) => ({
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  plan: getPlanData(state),
  planErknmStatus: getPlanDataErknmStatus(state),
  planInfo: getPlanInfo(state),
  isPrivilegesKno: getPlanIsPrivilegesKno(state),
  settings: getAdminFetchSettings(state),
  isWatchOnlyByTestUser: getPlanWatchOnlyByTestUser(state),
  formRef: getPlanFormRef(state),
});

export const PlanHeaderButtons = connect(mapsStateToProps)(PlanHeaderButtonsWithoutStore);
